import { parseBoard, replay } from "./board";
import {
  MAX_MOVES,
  type SokobanDefinition,
  type SokobanProgress,
  type SokobanSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Push every box onto a goal. You can only push, never pull, and only one box at a time: a box against a wall or another box will not move.",
  fr: "Poussez chaque caisse sur une cible. On pousse seulement, jamais on ne tire, et une seule caisse à la fois : une caisse contre un mur ou une autre caisse ne bouge pas.",
};

/** The same starting board as the backend's `empty_definition()`. */
export const STARTER_BOARD = [
  "#######",
  "#     #",
  "#  $  #",
  "#  @  #",
  "#  .  #",
  "#     #",
  "#######",
];

export function emptyDefinition(): SokobanDefinition {
  return {
    puzzleType: "sokoban",
    typeVersion: 1,
    board: [...STARTER_BOARD],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: SokobanDefinition): SokobanProgress {
  return { version: 1, moves: "", undone: "" };
}

function sanitize(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw)
    .filter((char) => "udlrUDLR".includes(char))
    .join("")
    .slice(0, MAX_MOVES);
}

/**
 * Accepts any stored blob (older versions, null, garbage) and returns a walkthrough this
 * board can actually play: a log recorded against another definition is cut at the first
 * move that no longer works, rather than leaving the board in a position nobody can reach.
 */
export function migrateProgress(definition: SokobanDefinition, raw: unknown): SokobanProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = raw as { moves?: unknown; undone?: unknown };
  const { board, state } = parseBoard(definition.board);
  const moves = sanitize(source.moves);
  if (!state) return { version: 1, moves: "", undone: "" };
  const outcome = replay(board, state, moves);
  return {
    version: 1,
    moves: outcome.moves,
    undone: outcome.illegalAt === null ? sanitize(source.undone) : "",
  };
}

/** The server replays the same string: what the player did, and nothing else. */
export function serializeSubmission(
  _definition: SokobanDefinition,
  progress: SokobanProgress,
): SokobanSubmission {
  return { moves: progress.moves };
}
